import type { Timestamp } from 'firebase/firestore';
import type { StageName } from '../lib/collections';

// appleUsers/{userId}/profiles/{profileId}/businessPlans/{planId}
// Matches iOS schema exactly.
export interface BusinessPlan {
  // Identity
  id: string;
  explorerName: string;
  businessName: string;

  // Progress
  currentStage: StageName | 'completed';
  completedStages: StageName[];
  stageProgress: Partial<Record<StageName, StageProgress>>;

  // Question responses — keyed by stageName → questionUUID → response string
  stageResponses: Partial<Record<StageName, Record<string, string>>>;

  // Homework: Stage 1 — Logo Builder
  logoKeywords?: string;
  selectedLogoColors?: string[];
  selectedLogoShape?: string;

  // Homework: Stage 2 — Problem Diary
  problemDiaryEntries?: ProblemDiaryEntry[];

  // Homework: Stage 3 — CEO Contract
  ceoBigPromise?: string;
  ceoCustomerFeeling?: string;
  ceoCeoOath?: string;

  // Homework: Stage 4 — Friend Survey
  friendSurveyResponses?: FriendSurveyResponse[];

  // Homework: Stage 5 — Would You Rather
  wouldYouRatherItems?: WouldYouRatherItem[];

  // Homework: Stage 6 — Elevator Pitch
  elevatorPitchWho?: string;
  elevatorPitchProblem?: string;
  elevatorPitchSolution?: string;
  elevatorPitchWhy?: string;

  // Profit Calculation
  profitCalculation?: {
    costToMake: number;
    sellingPrice: number;
  };

  // AI Feedback Cache (per-stage)
  aiFeedbackCache?: Partial<
    Record<
      StageName,
      {
        inputHash: string;
        feedback: string;
        generatedAt: Timestamp;
      }
    >
  >;

  // Generated Business Plan Document (after all 6 stages)
  businessPlanDocument?: BusinessPlanDocument;

  // Metadata
  createdAt: Timestamp;
  lastModified: Timestamp;
  isDeleted: boolean;
  deletedAt: Timestamp | null;
}

export interface StageProgress {
  isCompleted: boolean;
  questionsAnswered: number;
  totalQuestions: number;
  timeSpent: number; // seconds
  startedAt: Timestamp;
  completedAt: Timestamp | null;
}

/** @deprecated iOS-only type. Web uses color name strings with LOGO_COLOR_MAP. */
export interface LogoColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** Map of color names (as stored by LogoBuilder) to hex values. */
export const LOGO_COLOR_MAP: Record<string, string> = {
  'Black': '#000000',
  'Gray': '#6B7280',
  'Forest Green': '#166534',
  'Emerald': '#10B981',
  'Yellow': '#EAB308',
  'Coral': '#F97066',
  'White': '#FFFFFF',
  'Teal': '#14B8A6',
  'Navy Blue': '#1E3A5F',
  'Royal Blue': '#4169E1',
  'Purple': '#8B5CF6',
  'Magenta': '#D946EF',
  'Orange': '#F97316',
  'Red': '#EF4444',
  'Burgundy': '#7F1D1D',
  'Brown': '#92400E',
  'Lime': '#84CC16',
  'Mint': '#6EE7B7',
  'Sky Blue': '#38BDF8',
  'Pink': '#F472B6',
};

/** Resolve a stored logo color name to a hex string. */
export function logoColorToHex(name: string, fallback = '#6366f1'): string {
  return LOGO_COLOR_MAP[name] ?? fallback;
}

export interface ProblemDiaryEntry {
  ughMoment: string;
  location: 'School' | 'Home' | 'Somewhere else';
}

export interface FriendSurveyResponse {
  reaction: string;
  storeResponse: string;
  solvesProblem: string;
  whenUse: string;
  alternative: string;
}

export interface WouldYouRatherItem {
  name: string;
  price: number;
  category: 'Must-Have' | 'Nice-To-Have';
  currency: string;
}

export interface BusinessPlanDocument {
  businessPlanId: string;
  inputHash: string;
  fullDocument: string;
  introduction: string;
  stage1_OriginStory: string;
  stage2_StruggleIsReal: string;
  stage3_UghToAha: string;
  stage4_WhoBuysThis: string;
  stage5_ShowMeMoney: string;
  stage6_GamePlan: string;
  actionPlan: string;
  conclusion: string;
  totalWordCount: number;
}
